/**
 * Modal qui affiche les scans (devoir / examen / rattrapage) pour une matière.
 * Rassure l'étudiant en lui montrant la copie scannée par la scolarité.
 */
import React, { useEffect, useState } from 'react';
import { X, FileSearch, AlertCircle, Loader2, ZoomIn, ZoomOut, AlertTriangle } from 'lucide-react';
import { portailApi } from '../services/portailApi';

interface ScanItem {
  type: 'devoir' | 'examen' | 'rattrapage';
  libelle: string;
  image_base64: string | null;
  numero_ligne: number | null;
  numero_page: number | null;
  code_releve: string;
  date_scan: string | null;
}

interface Props {
  matiereId: number;
  matiereNom: string;
  onClose: () => void;
  onReclamation?: (typeRec: 'devoir' | 'examen') => void;
  // Mode auto-affichage : si fourni, ouvre directement l'onglet correspondant
  filtreInitial?: 'devoir' | 'examen' | 'rattrapage';
}

export const ScanCopiesModal: React.FC<Props> = ({
  matiereId, matiereNom, onClose, onReclamation, filtreInitial,
}) => {
  const [loading, setLoading] = useState(true);
  const [scans, setScans] = useState<ScanItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'devoir' | 'examen' | 'rattrapage'>('devoir');
  const [zoom, setZoom] = useState(1);
  const [scansDesactives, setScansDesactives] = useState(false);
  const [messageDesactivation, setMessageDesactivation] = useState<string>('');

  useEffect(() => {
    let mounted = true;
    portailApi.getCopiesScan(matiereId)
      .then((res) => {
        if (!mounted) return;
        if (res.scans_desactives) {
          setScansDesactives(true);
          setMessageDesactivation(res.message || 'Consultation temporairement indisponible.');
          return;
        }
        const list = (res.scans || []) as ScanItem[];
        setScans(list);
        const ordre: Array<'devoir' | 'examen' | 'rattrapage'> = ['devoir', 'examen', 'rattrapage'];
        const candidat = filtreInitial && list.find((s) => s.type === filtreInitial)
          ? filtreInitial
          : ordre.find((t) => list.some((s) => s.type === t)) || 'devoir';
        setTab(candidat);
      })
      .catch((e: any) => setError(e?.message || 'Erreur de chargement'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [matiereId, filtreInitial]);

  const scanCourant = scans.find((s) => s.type === tab);
  const typesDispos = Array.from(new Set(scans.map((s) => s.type)));

  const TabBtn = ({ t, label }: { t: 'devoir' | 'examen' | 'rattrapage'; label: string }) => {
    const dispo = typesDispos.includes(t);
    const active = tab === t;
    return (
      <button
        onClick={() => dispo && setTab(t)}
        disabled={!dispo}
        className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
          active
            ? 'bg-blue-600 text-white'
            : dispo
              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              : 'bg-gray-900 text-gray-600 cursor-not-allowed'
        }`}
      >
        {label}
        {!dispo && <span className="ml-1 text-[10px] opacity-60">(non dispo)</span>}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-gray-900 w-full sm:max-w-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3">
          <FileSearch className="w-5 h-5 text-blue-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-100 text-sm">Vérification de mes copies</div>
            <div className="text-xs text-gray-400 truncate">{matiereNom}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 p-1">
            <X size={20} />
          </button>
        </div>

        {/* Onglets */}
        <div className="px-4 py-2 border-b border-gray-800 grid grid-cols-3 gap-1.5">
          <TabBtn t="devoir" label="Devoir" />
          <TabBtn t="examen" label="Examen" />
          <TabBtn t="rattrapage" label="Rattrapage" />
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="inline-block w-8 h-8 animate-spin text-blue-500" />
              <p className="text-gray-400 mt-3 text-sm">Chargement des copies...</p>
            </div>
          ) : error ? (
            <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 text-sm text-red-300">
              {error}
            </div>
          ) : scansDesactives ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-blue-400 mb-3" />
              <h4 className="font-semibold text-gray-100">Consultation temporairement indisponible</h4>
              <p className="text-sm text-gray-400 mt-2 max-w-sm mx-auto">{messageDesactivation}</p>
            </div>
          ) : scans.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-orange-500 mb-3" />
              <h4 className="font-semibold text-gray-100">Aucun scan disponible</h4>
              <p className="text-sm text-gray-400 mt-2 max-w-sm mx-auto">
                Le relevé pour cette matière n'a pas été scanné. Pour vérifier votre note,
                présentez-vous au service de la scolarité avec votre carte d'étudiant.
              </p>
            </div>
          ) : !scanCourant?.image_base64 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-orange-500 mb-3" />
              <h4 className="font-semibold text-gray-100">
                {tab === 'devoir' && 'Devoir non scanné'}
                {tab === 'examen' && 'Examen non scanné'}
                {tab === 'rattrapage' && 'Rattrapage non disponible'}
              </h4>
              <p className="text-sm text-gray-400 mt-2 max-w-sm mx-auto">
                Pour cette matière, le relevé {tab === 'devoir' ? 'de devoirs' : tab === 'examen' ? 'd\'examen' : 'de rattrapage'}
                {' '}n'a pas été scanné. Présentez-vous au service de la scolarité pour consultation.
              </p>
            </div>
          ) : (
            <div>
              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-3 mb-3 text-sm text-blue-200 flex gap-2">
                <FileSearch className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">{scanCourant.libelle}</div>
                  <div className="text-xs text-blue-300/80 mt-0.5">
                    {scanCourant.code_releve}
                    {scanCourant.numero_page && ` · Page ${scanCourant.numero_page}`}
                    {scanCourant.numero_ligne && ` · Ligne ${scanCourant.numero_ligne}`}
                  </div>
                </div>
              </div>

              {/* Contrôles zoom */}
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="text-xs text-gray-400">Zoom : {Math.round(zoom * 100)}%</div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}
                    className="p-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded"
                  >
                    <ZoomOut size={14} />
                  </button>
                  <button
                    onClick={() => setZoom(1)}
                    className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded text-xs"
                  >
                    100%
                  </button>
                  <button
                    onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
                    className="p-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded"
                  >
                    <ZoomIn size={14} />
                  </button>
                </div>
              </div>

              {/* Image */}
              <div className="bg-white rounded overflow-auto border border-gray-700 max-h-[55vh]">
                <img
                  src={`data:image/jpeg;base64,${scanCourant.image_base64}`}
                  alt={scanCourant.libelle}
                  style={{
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top left',
                    display: 'block',
                  }}
                  className="w-full"
                />
              </div>

              <p className="text-xs text-gray-500 mt-3 italic text-center">
                Voici la copie scannée enregistrée par la scolarité. La note affichée correspond à ce qui figure sur cette copie.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-800 space-y-2">
          {!loading && onReclamation && (
            <button
              onClick={() => {
                // Si aucun scan : on tombe par defaut sur 'examen' (l'utilisateur
                // choisira devoir/examen dans la modal de reclamation).
                const typeRec = scans.length === 0
                  ? 'examen'
                  : tab === 'rattrapage' ? 'examen' : tab;
                onReclamation(typeRec);
                onClose();
              }}
              className="w-full py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg text-sm inline-flex items-center justify-center gap-2"
            >
              <AlertTriangle size={16} />
              {scans.length === 0
                ? 'Déposer une réclamation'
                : 'Ma note diffère — Déposer une réclamation'}
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg text-sm font-medium"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};
